import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useServiceStore } from '../../stores/useServiceStore';
import { useCurrentStore } from '../../hooks/useCurrentStore';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell, Button, ConfirmModal } from '@redpanda/ui';
import ServiceDialog from '../../components/ServiceDialog';

const ServicesPage: React.FC = () => {
  const { t } = useTranslation(['pages', 'translation']);
  const { storeId } = useCurrentStore(); // 自動獲取當前用戶店鋪
  const {
    services,
    isLoading,
    hasFetched,
    showDeleteConfirmModal,
    fetchServices,
    createService,
    updateService,
    toggleServiceStatus,
    handleDelete,
    confirmDelete,
    closeDeleteModal,
  } = useServiceStore();

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<any>(null);

  // 載入服務項目（當店鋪資訊載入完成後）
  useEffect(() => {
    if (storeId) {
      fetchServices(storeId);
    }
  }, [storeId, fetchServices]);

  const handleAddService = () => {
    setSelectedService(null);
    setIsDialogOpen(true);
  };

  const handleEditService = (service: any) => {
    setSelectedService(service);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedService(null);
  };

  const handleSubmit = async (data: any) => {
    if (!storeId) return;

    if (selectedService) {
      await updateService(selectedService.id, data);
    } else {
      await createService(storeId, data);
    }
  };

  const handleToggleStatus = async (service: any) => {
    try {
      await toggleServiceStatus(service.id, !service.isActive);
    } catch (error) {
      // Error handled in store
    }
  };

  return (
    <div className="container mx-auto px-4 md:px-10 py-6 pb-32">
      <div className="mb-4">
        <div className="flex items-baseline justify-between">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
            {t('pages:admin.services.title')}
          </h1>
          <Button
            onClick={handleAddService}
            className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
          >
            {t('pages:admin.services.addService')}
          </Button>
        </div>
      </div>

      {/* 只在首次載入且尚未讀取過時顯示載入畫面 */}
      {isLoading && services.length === 0 && !hasFetched ? (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          {t('pages:admin.services.loading')}
        </div>
      ) : services.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            {t('pages:admin.services.noRecords')}
          </p>
          <Button
            onClick={handleAddService}
            className="bg-brand-600 hover:bg-brand-700 text-white font-bold py-2 px-4 rounded transition-all duration-200"
          >
            {t('pages:admin.services.addFirstService')}
          </Button>
        </div>
      ) : (
        <>
          {/* 手機版卡片列表 */}
          <div className="md:hidden space-y-3">
            {services.map((service) => (
              <div
                key={service.id}
                className={`bg-white dark:bg-slate-800 shadow rounded-lg p-3 space-y-3 ${
                  !service.isActive ? 'opacity-50' : ''
                }`}
              >
                {/* 服務名稱和狀態 */}
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                      {service.name}
                    </h3>
                    {service.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                        {service.description}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => handleToggleStatus(service)}
                    className={`flex-shrink-0 px-2 py-1 rounded text-xs font-medium cursor-pointer transition-colors ${
                      service.isActive
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800'
                        : 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                    }`}
                  >
                    {service.isActive ? t('pages:admin.services.enabled') : t('pages:admin.services.disabled')}
                  </button>
                </div>

                {/* 服務詳情 */}
                <div className="grid grid-cols-3 gap-3 text-center border-t border-gray-200 dark:border-slate-700 pt-3">
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('pages:admin.services.mobileView.price')}</p>
                    <p className="text-sm font-semibold text-brand-600 dark:text-brand-400">
                      ${Math.floor(Number(service.price))}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('pages:admin.services.mobileView.duration')}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      {service.duration} {t('pages:admin.services.mobileView.minutes')}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('pages:admin.services.mobileView.category')}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                      {service.category || '-'}
                    </p>
                  </div>
                </div>

                {/* 操作按鈕 */}
                <div className="flex space-x-4 border-t border-gray-200 dark:border-slate-700 pt-3">
                  <button
                    onClick={() => handleEditService(service)}
                    className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300 font-medium"
                  >
                    {t('translation:common.edit')}
                  </button>
                  <button
                    onClick={() => handleDelete(service.id)}
                    className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 font-medium"
                  >
                    {t('translation:common.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* 桌面版表格 */}
          <div className="hidden md:block bg-white dark:bg-slate-800 shadow overflow-hidden rounded-md">
            <div className="overflow-x-auto">
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/6">{t('pages:admin.services.tableHeaders.serviceName')}</TableHead>
                  <TableHead className="w-2/5">{t('pages:admin.services.tableHeaders.description')}</TableHead>
                  <TableHead className="w-1/12">{t('pages:admin.services.tableHeaders.price')}</TableHead>
                  <TableHead className="w-1/12">{t('pages:admin.services.tableHeaders.duration')}</TableHead>
                  <TableHead className="w-1/12">{t('pages:admin.services.tableHeaders.category')}</TableHead>
                  <TableHead className="w-1/12">{t('pages:admin.services.tableHeaders.status')}</TableHead>
                  <TableHead className="w-1/6">{t('pages:admin.services.tableHeaders.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((service) => (
                  <TableRow
                    key={service.id}
                    className={!service.isActive ? 'opacity-50' : ''}
                  >
                    <TableCell className="font-medium">
                      {service.name}
                    </TableCell>
                    <TableCell>
                      {service.description || '-'}
                    </TableCell>
                    <TableCell>${Math.floor(Number(service.price))}</TableCell>
                    <TableCell>{service.duration} {t('pages:admin.services.mobileView.minutes')}</TableCell>
                    <TableCell>{service.category || '-'}</TableCell>
                    <TableCell>
                      <button
                        onClick={() => handleToggleStatus(service)}
                        className={`px-2 py-1 rounded text-xs font-medium cursor-pointer transition-colors ${
                          service.isActive
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800'
                            : 'bg-gray-100 text-gray-800 dark:bg-slate-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
                        }`}
                      >
                        {service.isActive ? t('pages:admin.services.enabled') : t('pages:admin.services.disabled')}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex space-x-4">
                        <button
                          onClick={() => handleEditService(service)}
                          className="text-brand-600 hover:text-brand-900 dark:text-brand-400 dark:hover:text-brand-300"
                        >
                          {t('translation:common.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(service.id)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        >
                          {t('translation:common.delete')}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
        </>
      )}

      {/* 服務對話框 */}
      <ServiceDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        service={selectedService}
        storeId={storeId}
      />

      {/* 刪除確認對話框 */}
      {showDeleteConfirmModal && (
        <ConfirmModal
          isOpen={showDeleteConfirmModal}
          onClose={closeDeleteModal}
          onConfirm={confirmDelete}
          title={t('pages:admin.services.confirmDelete.title')}
          message={t('pages:admin.services.confirmDelete.message')}
          confirmText={t('pages:admin.services.confirmDelete.confirm')}
          cancelText={t('pages:admin.services.confirmDelete.cancel')}
          type="danger"
        />
      )}
    </div>
  );
};

export default ServicesPage;
